import { useCallback, useEffect, useRef, useState } from 'react';

/** Loads data and exposes reload(); keeps the last good data while reloading */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fnRef.current());
      setError(null);
    } catch (e) {
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => void reload(), deps);
  return { data, error, loading, reload, setData };
}

/** Subscribes to server-sent events and calls onChange (debounced) */
export function useLiveUpdates(url: string, onChange: () => void) {
  const [connected, setConnected] = useState(false);
  const cb = useRef(onChange);
  cb.current = onChange;
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const es = new EventSource(url, { withCredentials: true });
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener('change', () => {
      clearTimeout(timer);
      timer = setTimeout(() => cb.current(), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, [url]);
  return connected;
}

/** Re-runs fn on an interval while the tab is visible */
export function usePoll(fn: () => void, ms: number) {
  const cb = useRef(fn);
  cb.current = fn;
  useEffect(() => {
    const id = setInterval(() => document.visibilityState === 'visible' && cb.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}
